import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ProductApi } from '../apis/product-api';
import CloseableErrorMessage from '../components/CloseableErrorMessage';
import { CompanyFeatureSyncInput } from '../models/company-feature-sync-input-interface';
import { Feature, ProductVersionModel } from '../models/product-interface';

export const ProductQueries = {
  useProducts: () =>
    useQuery({
      queryKey: ['products'],
      queryFn: async () => await ProductApi.getProducts(),
      enabled: true,
      onError: () => CloseableErrorMessage('Error getting products.'),
    }),
  useActiveProducts: () =>
    useQuery({
      queryKey: ['activeProducts'],
      queryFn: async () => await ProductApi.getActiveProducts(),
      onError: () => CloseableErrorMessage('Error getting product information.'),
    }),
  useRatePlanForProduct: (productId: number) =>
    useQuery({
      queryKey: ['productRatePlan', productId],
      queryFn: async () => await ProductApi.getRatePlanForProduct(productId),
      enabled: !!productId,
      // onError: () => CloseableErrorMessage('Error getting product rate plan information.'),
    }),
  useCompanyProducts: (companyId: number | undefined, deleted: boolean = true) =>
    useQuery({
      queryKey: ['companyProducts', companyId],
      queryFn: async () => await ProductApi.getCompanyProducts(companyId!),
      enabled: !!companyId && !deleted,
      onError: () => CloseableErrorMessage('Error getting products.'),
    }),
  useProductDetail: (productId: number) =>
    useQuery({
      queryKey: ['productId', productId],
      queryFn: async () => await ProductApi.getProductVersions(productId),
      enabled: !!productId,
      onError: () => CloseableErrorMessage('Error getting product.'),
    }),
  useFeaturesList: () => 
    useQuery({
      queryKey: ['allFeatures'],
      queryFn: async () => await ProductApi.getAllFeatures(),
      onError: () => CloseableErrorMessage('Error getting features.'),
      staleTime: 600000,
    }),
  useAddProductFeatures: (productId: number, versionId: number) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async (data: Feature[]) => await ProductApi.postProductBulkFeatures(productId, versionId, data),
      onError: () => CloseableErrorMessage('Error adding features to product.'),
      onSuccess: () => {
        queryClient.invalidateQueries({queryKey: ['productId', productId]});
      }
    })
  },
  useDeleteProductFeatures: (productId: number, versionId: number) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async (data: Feature[]) => await ProductApi.deleteProductFeatures(productId, versionId, data),
      onError: () => CloseableErrorMessage('Error removing features to product.'),
      onSuccess: () => {
        queryClient.invalidateQueries({queryKey: ['productId', productId]});
      }
    })
  },
  useAddProductVersion: (productId: number) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async (data: ProductVersionModel) => await ProductApi.postProductNewVersion(productId, data),
      onError: () => CloseableErrorMessage('Error importing users.'),
      onSuccess: () => {
        queryClient.invalidateQueries({queryKey: ['productId', productId]});
      }
    })
  },
  useSyncFeaturesWithVersion: (productId: number) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async (data: CompanyFeatureSyncInput) => await ProductApi.postSyncFeaturesWithVersion(data),
      onSuccess: () => {
        queryClient.invalidateQueries({queryKey: ['productId', productId]});
      }
    })
  },
};
